import {AppBar, Accordion, AccordionSummary, IconButton, Toolbar, Typography, Dialog} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import React, {useEffect} from 'react';
import {EmployeeErrorDetail} from 'Enrollment/models/EmployeeErrorDetail';
import Column from 'Shared/components/PagedView/Column';
import PagedView from 'Shared/components/PagedView/PagedView';
import {fromResult, toPage} from 'Core/models/Async';
import './GenelcoSpreadsheetFileErrorDetailsDialog.sass';
import {withStringFilterProperties} from 'Shared/components/PagedView/Filter';
import {withValueChangedHook} from 'Shared/utilities/withValueChanged';
import {
  getEnrollmentErrorDetails,
  changeEnrollmentApplicationErrorDetailFilters,
  changeSort,
  clearSort,
} from 'Enrollment/ducks/enrollmentFileProcessing';
import {useSelector, useDispatch} from 'react-redux';
import {ReducerState} from 'rootReducer';
import * as O from 'fp-ts/lib/Option';

type GenelcoSpreadsheetFileErrorDetailsDialogProps = {
  dialogOpen: boolean;
  dialogClose: () => void;
  employeeErrorDetails: EmployeeErrorDetail[];
  enrollmentFileId: string;
  fileName: string;
  applicationCount: number;
  applicationErrorCount: number;
  uploadedDate: string;
};

type errorTypes = {
  firstName: string;
  lastName: string;
  ssn: string;
  product: string;
  errorType: string;
};
const GenelcoSpreadsheetFileErrorDetailsDialog = (props: GenelcoSpreadsheetFileErrorDetailsDialogProps) => {
  const dispatch = useDispatch();
  const {enrollmentApplicationErrorDetailFilters, sort} = useSelector(
    (state: ReducerState) => state.enrollmentFileProcessing
  );
  const changeFilter = withValueChangedHook(
    enrollmentApplicationErrorDetailFilters,
    changeEnrollmentApplicationErrorDetailFilters,
    dispatch
  );
  const filterProperties = {
    changeFilter,
    filterData: enrollmentApplicationErrorDetailFilters,
  };

  useEffect(() => {
    dispatch(
      getEnrollmentErrorDetails(
        props.enrollmentFileId!,
        {
          ...enrollmentApplicationErrorDetailFilters,
          id: props.enrollmentFileId!,
        },
        O.toUndefined(sort)
      )
    );
  }, [dispatch, enrollmentApplicationErrorDetailFilters, sort, props.enrollmentFileId]);

  useEffect(() => {
    dispatch(clearSort());
  }, [dispatch]);

  const getErrorDetails = (employeeErrorDetails: EmployeeErrorDetail[]) => {
    const errorTypes = new Array<errorTypes>();
    employeeErrorDetails.forEach(detail =>
      detail.errors?.forEach(error => {
        errorTypes.push({
          firstName: detail.firstName!,
          lastName: detail.lastName!,
          ssn: detail.maskedSSN!,
          product: detail.product!,
          errorType: error,
        });
      })
    );
    employeeErrorDetails.forEach(detail =>
      detail.auditErrors?.forEach(error => {
        errorTypes.push({
          firstName: detail.firstName!,
          lastName: detail.lastName!,
          ssn: detail.maskedSSN!,
          product: detail.product!,
          errorType: error,
        });
      })
    );
    employeeErrorDetails.forEach(detail =>
      detail.kickoutErrors?.forEach(error => {
        errorTypes.push({
          firstName: detail.firstName!,
          lastName: detail.lastName!,
          ssn: detail.maskedSSN!,
          product: detail.product!,
          errorType: error,
        });
      })
    );
    employeeErrorDetails.forEach(detail =>
      detail.dependentValidationErrors?.forEach(error => {
        errorTypes.push({
          firstName: detail.firstName!,
          lastName: detail.lastName!,
          ssn: detail.maskedSSN!,
          product: detail.product!,
          errorType: error,
        });
      })
    );
    employeeErrorDetails.forEach(detail =>
      detail.dependentAuditErrors?.forEach(error => {
        errorTypes.push({
          firstName: detail.firstName!,
          lastName: detail.lastName!,
          ssn: detail.maskedSSN!,
          product: detail.product!,
          errorType: error,
        });
      })
    );
    employeeErrorDetails.forEach(detail =>
      detail.groupComparisonError?.forEach(error => {
        errorTypes.push({
          firstName: detail.firstName!,
          lastName: detail.lastName!,
          ssn: detail.maskedSSN!,
          product: detail.product!,
          errorType: error,
        });
      })
    );
    employeeErrorDetails.forEach(detail =>
      detail.batchFileCreationErrors?.forEach(error => {
        errorTypes.push({
          firstName: detail.firstName!,
          lastName: detail.lastName!,
          ssn: detail.maskedSSN!,
          product: detail.product!,
          errorType: error,
        });
      })
    );
    return errorTypes;
  };
  return (
    <Dialog fullScreen open={props.dialogOpen} onClose={props.dialogClose}>
      <AppBar className="genelco-spreadsheet-file-section__dialog-header">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={props.dialogClose} aria-label="close">
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" className="genelco-spreadsheet-file-section__dialog-title">
            Unsuccessful Employees
          </Typography>
        </Toolbar>
      </AppBar>

      <div className="genelco-spreadsheet-file-section ">
        <Toolbar className="genelco-spreadsheet-file-errors-table">
          <Typography
            id="genelco-spreadsheet-file-errors-title"
            className="genelco-spreadsheet-file-errors-table__title"
            variant="h6"
          >
            Error Details
          </Typography>
        </Toolbar>
        <Accordion className="file-summary-line-info__expansion-panel">
          <AccordionSummary>
            <div className="summary-line-info__expansion-panel__icon-container">
              <i className="icon-container__file-icon"></i>
            </div>
            <div className="summary-line-info__expansion-panel__file-details">
              <div className="summary-line-info__expansion-panel__file-details--content">
                <span className="title">File Name:</span>
                <span className="value">
                    {props.fileName!}
                </span>
              </div>
              <div className="summary-line-info__expansion-panel__file-details--content">
                <span className="title">Uploaded:</span>
                <span className="value">{props.uploadedDate!}</span>
              </div>
              <div className="summary-line-info__expansion-panel__file-details--content">
                <span className="title">Application Count:</span>
                <span className="value">{props.applicationCount!}</span>
              </div>
              <div className="summary-line-info__expansion-panel__file-details--content">
                <span className="title">Applications with Errors:</span>
                <span className="value">{props.applicationErrorCount!}</span>
              </div>
            </div>
          </AccordionSummary>
        </Accordion>
      </div>
      <PagedView
        pagedViewKey="genelcoSpreadsheetFileError"
        data={fromResult(toPage(getErrorDetails(props.employeeErrorDetails)))}
        sort={O.toUndefined(sort)}
        onChangeSort={sort => dispatch(changeSort(sort))}
        loadMore={() => {}}
      >
        {columnProps => (
          <>
            <Column
              {...columnProps}
              title="First Name"
              sortColumnKey="firstName"
              filterProperties={withStringFilterProperties(filterProperties, 'firstName')}
              default
            >
              {employeeErrorDetail => employeeErrorDetail.firstName}
            </Column>
            <Column
              {...columnProps}
              title="Last Name"
              sortColumnKey="lastName"
              filterProperties={withStringFilterProperties(filterProperties, 'lastName')}
              default
            >
              {employeeErrorDetail => employeeErrorDetail.lastName}
            </Column>
            <Column
              {...columnProps}
              title="SSN"
              sortColumnKey="ssn"
              filterProperties={withStringFilterProperties(filterProperties, 'ssn')}
              default
            >
              {employeeErrorDetail => employeeErrorDetail.ssn}
            </Column>
            <Column
              {...columnProps}
              title="Product"
              sortColumnKey="product"
              filterProperties={withStringFilterProperties(filterProperties, 'product')}
              default
            >
              {employeeErrorDetail => employeeErrorDetail.product}
            </Column>
            <Column
              {...columnProps}
              title="Error Type"
              filterProperties={withStringFilterProperties(filterProperties, 'errorType')}
              default
            >
              {employeeErrorDetail => employeeErrorDetail.errorType}
            </Column>
          </>
        )}
      </PagedView>
    </Dialog>
  );
};

export default GenelcoSpreadsheetFileErrorDetailsDialog;
